import type { Language } from "../domain/shared/localized-text.types";

export const associationContent = {
  es: {
    slogan: "Asociación de Productores Prestadores de Servicios y Turismo del Pacífico Sur",
    description:
      "La Asociación APINATIVOS es una organización comunitaria ubicada en Coto Brus, Puntarenas, Costa Rica, que promueve el desarrollo sostenible mediante el fortalecimiento de iniciativas locales en turismo rural, producción agroecológica y conservación ambiental.",
    history:
      "Don Gilbert, ebanista de profesión, conoció al apicultor Carlos Azofeifa, quien le pidió fabricar cajas para sus colmenas. De ahí nació su pasión por la apicultura y, con el tiempo, el proceso que culminó en la creación oficial de APINATIVOS en agosto de 2023.",
    mission:
      "Promover el desarrollo sostenible mediante el fortalecimiento de iniciativas locales en turismo rural, producción agroecológica y conservación ambiental.",
    vision:
      "Ser una organización líder en el desarrollo comunitario sostenible del Pacífico Sur costarricense, reconocida por su compromiso con la conservación ambiental y el empoderamiento de las comunidades locales.",
    galleryCaption: "Momentos importantes de nuestra asociación",
  },
  en: {
    slogan: "Association of Producers, Service Providers, and Tourism of the South Pacific",
    description:
      "APINATIVOS Association is a community-based organization in Coto Brus, Puntarenas, Costa Rica, promoting sustainable development by strengthening local initiatives in rural tourism, agroecological production, and environmental conservation.",
    history:
      "Don Gilbert, a professional woodworker, met beekeeper Carlos Azofeifa, who asked him to build hive boxes. That request sparked his passion for beekeeping and eventually led to the formal creation of APINATIVOS in August 2023.",
    mission:
      "Promote sustainable development by strengthening local initiatives in rural tourism, agroecological production, and environmental conservation.",
    vision:
      "Become a leading organization in sustainable community development across Costa Rica's South Pacific, recognized for environmental stewardship and community empowerment.",
    galleryCaption: "Meaningful moments from our association",
  },
} as const;

export const uiText = {
  es: {
    nav: {
      home: "Inicio",
      about: "Quiénes Somos",
      gallery: "Galería",
      products: "Productos",
      contact: "Contáctenos",
      businesses: "Empresas",
      backToAssociation: "Volver a Asociación",
      openMenu: "Abrir menú",
      language: "Idioma",
      auto: "Auto",
    },
    footer: {
      contact: "Contacto",
      quickLinks: "Enlaces Rápidos",
      followUs: "Síguenos",
      website: "Sitio web",
      rightsReserved: "Todos los derechos reservados.",
    },
    home: {
      welcome: "Bienvenidos a {name}",
      learnMore: "Conozca más sobre nosotros",
      businessesTitle: "Nuestras Empresas",
      interestedTitle: "¿Interesado en nuestros productos y servicios?",
      interestedBody:
        "Estamos aquí para responder sus preguntas y ayudarle a encontrar lo que necesita.",
      contactToday: "Contáctenos hoy",
      seeMore: "Ver más",
    },
    about: {
      heroTitle: "Quiénes Somos",
      history: "Nuestra Historia",
      mission: "Misión",
      vision: "Visión",
      communityTitle: "¿Quieres ser parte de nuestra comunidad?",
      communityBody:
        "Únete a nosotros en nuestra misión de promover el desarrollo sostenible y la conservación ambiental en el Pacífico Sur costarricense.",
      followFacebook: "Síguenos en Facebook",
    },
    contact: {
      heroTitle: "Contáctenos - {name}",
      heroSubtitle: "Estamos para servirle",
      infoTitle: "Información de Contacto",
      address: "Dirección",
      phone: "Teléfono",
      email: "Correo electrónico",
      name: "Nombre",
      fullName: "Tu nombre completo",
      message: "Mensaje",
      messagePlaceholder: "Escribe tu mensaje aquí...",
      send: "Enviar mensaje",
      sending: "Enviando...",
      success: "Mensaje enviado correctamente. Te contactaremos pronto.",
      whatsapp: "Escríbenos por WhatsApp",
      phoneHint: "Ej: +506 8888 8888 • Puedes elegir cualquier país.",
      phonePlaceholder: "Incluye tu número con código de país",
      emailPlaceholder: "ejemplo@correo.com",
      errors: {
        name: "Por favor ingresa tu nombre.",
        email: "Por favor ingresa un correo electrónico válido.",
        phone: "Por favor ingresa un teléfono válido, incluyendo el código de país.",
        message: "Por favor ingresa un mensaje.",
        endpoint: "El endpoint del formulario no está configurado.",
        submit: "Hubo un error al enviar el mensaje.",
      },
    },
    gallery: {
      heroTitle: "Galería",
      heroSubtitle: "Nuestros momentos y logros",
      title: "Imágenes de Nuestra Asociación",
      body:
        "Descubre momentos importantes, proyectos y la belleza natural del Pacífico Sur costarricense a través de esta galería.",
      shareTitle: "¿Quieres compartir tus fotos con nosotros?",
      shareBody:
        "Si has visitado nuestras empresas asociadas o participado en nuestras actividades, nos encantaría ver tus fotos y experiencias.",
      contact: "Contactar",
    },
    business: {
      notFound: "Empresa no encontrada",
      notFoundBody: "La empresa que estás buscando no existe.",
      welcome: "Bienvenidos a {name}",
      viewProducts: "Ver nuestros productos",
      contactUs: "Contáctanos",
      facilities: "Nuestras Instalaciones",
      facilitiesBody: "Conoce los espacios donde creamos nuestros productos",
      noFacilityImages: "No hay imágenes de instalaciones disponibles",
      viewFullGallery: "Ver galería completa",
      catalogTitle: "Catálogo de Productos",
      catalogBody: "Algunos de nuestros productos destacados",
      from: "Desde {price}",
      askPrice: "Consultar precio",
      askWhatsapp: "Consultar",
      viewFullCatalog: "Ver catálogo completo",
      multiPresentationHelp:
        "Este producto tiene varias presentaciones. Puedes escribir por WhatsApp o ver el catálogo completo para más detalle.",
      howToFindUs: "¿Cómo encontrarnos?",
      locationUnavailable: "Ubicación no disponible",
      openDirections: "Abrir navegación",
      aboutHero: "Sobre {name}",
      aboutSubtitle: "Nuestra historia y valores",
      whoWeAre: "¿Quiénes Somos?",
      galleryHero: "Galería de {name}",
      gallerySubtitle: "Conoce nuestros espacios y servicios",
      offerTitle: "Nuestra Oferta",
      featuredImages: "Imágenes Destacadas",
      fullGallery: "Galería Completa",
      noImages: "No hay imágenes disponibles actualmente",
      imagesSoon: "Pronto agregaremos fotos de nuestras instalaciones",
      videos: "Videos",
      videosBody: "Conoce más sobre nosotros a través de nuestros videos",
      comingSoon: "Próximamente",
      productsHero: "Productos de {name}",
      productsSubtitle: "Calidad artesanal en cada detalle",
      productCatalog: "Nuestro Catálogo",
      productCatalogBody: "Descubre la excelencia de nuestros productos cuidadosamente elaborados",
      viewProduct: "Ver producto",
      choosePresentation: "Elegir presentación",
      consult: "Consultar",
      presentation: "Presentación",
      chooseVariantLabelWeight: "Peso o tamaño",
      chooseVariantLabelFlavor: "Sabor",
      decreaseQty: "Disminuir cantidad",
      increaseQty: "Aumentar cantidad",
      quantity: "Cantidad",
      contactWhatsapp: "Consultar",
      noProducts: "No hay productos disponibles",
      productsSoon: "Pronto tendremos novedades en nuestro catálogo",
      backHome: "Volver a la página principal",
    },
  },
  en: {
    nav: {
      home: "Home",
      about: "About Us",
      gallery: "Gallery",
      products: "Products",
      contact: "Contact",
      businesses: "Businesses",
      backToAssociation: "Back to Association",
      openMenu: "Open menu",
      language: "Language",
      auto: "Auto",
    },
    footer: {
      contact: "Contact",
      quickLinks: "Quick Links",
      followUs: "Follow Us",
      website: "Website",
      rightsReserved: "All rights reserved.",
    },
    home: {
      welcome: "Welcome to {name}",
      learnMore: "Learn more about us",
      businessesTitle: "Our Businesses",
      interestedTitle: "Interested in our products and services?",
      interestedBody:
        "We are here to answer your questions and help you find what you need.",
      contactToday: "Contact us today",
      seeMore: "See more",
    },
    about: {
      heroTitle: "About Us",
      history: "Our History",
      mission: "Mission",
      vision: "Vision",
      communityTitle: "Would you like to be part of our community?",
      communityBody:
        "Join us in our mission to promote sustainable development and environmental conservation in Costa Rica's South Pacific.",
      followFacebook: "Follow us on Facebook",
    },
    contact: {
      heroTitle: "Contact - {name}",
      heroSubtitle: "We are here to help",
      infoTitle: "Contact Information",
      address: "Address",
      phone: "Phone",
      email: "Email",
      name: "Name",
      fullName: "Your full name",
      message: "Message",
      messagePlaceholder: "Write your message here...",
      send: "Send message",
      sending: "Sending...",
      success: "Your message was sent successfully. We'll contact you soon.",
      whatsapp: "Message us on WhatsApp",
      phoneHint: "Example: +506 8888 8888 • You can choose any country.",
      phonePlaceholder: "Include your number with country code",
      emailPlaceholder: "example@email.com",
      errors: {
        name: "Please enter your name.",
        email: "Please enter a valid email address.",
        phone: "Please enter a valid phone number, including country code.",
        message: "Please enter a message.",
        endpoint: "The contact form endpoint is not configured.",
        submit: "There was an error sending your message.",
      },
    },
    gallery: {
      heroTitle: "Gallery",
      heroSubtitle: "Our moments and achievements",
      title: "Images from Our Association",
      body:
        "Explore important moments, projects, and the natural beauty of Costa Rica's South Pacific through this gallery.",
      shareTitle: "Would you like to share your photos with us?",
      shareBody:
        "If you have visited our partner businesses or joined our activities, we would love to see your photos and experiences.",
      contact: "Get in touch",
    },
    business: {
      notFound: "Business not found",
      notFoundBody: "The business you are looking for does not exist.",
      welcome: "Welcome to {name}",
      viewProducts: "View our products",
      contactUs: "Contact us",
      facilities: "Our Facilities",
      facilitiesBody: "Get to know the spaces where our products are created",
      noFacilityImages: "No facility images are available",
      viewFullGallery: "View full gallery",
      catalogTitle: "Product Catalog",
      catalogBody: "Some of our featured products",
      from: "From {price}",
      askPrice: "Request price",
      askWhatsapp: "Inquire",
      viewFullCatalog: "View full catalog",
      multiPresentationHelp:
        "This product has multiple options. You can message us on WhatsApp or open the full catalog for more details.",
      howToFindUs: "How to find us",
      locationUnavailable: "Location not available",
      openDirections: "Open directions",
      aboutHero: "About {name}",
      aboutSubtitle: "Our story and values",
      whoWeAre: "Who We Are",
      galleryHero: "Gallery of {name}",
      gallerySubtitle: "Discover our spaces and services",
      offerTitle: "What We Offer",
      featuredImages: "Featured Images",
      fullGallery: "Full Gallery",
      noImages: "No images are currently available",
      imagesSoon: "We will add photos of our facilities soon",
      videos: "Videos",
      videosBody: "Get to know us better through our videos",
      comingSoon: "Coming soon",
      productsHero: "Products from {name}",
      productsSubtitle: "Artisanal quality in every detail",
      productCatalog: "Our Catalog",
      productCatalogBody: "Discover the excellence of our carefully crafted products",
      viewProduct: "View product",
      choosePresentation: "Choose option",
      consult: "Inquire",
      presentation: "Presentation",
      chooseVariantLabelWeight: "Weight or size",
      chooseVariantLabelFlavor: "Flavor",
      decreaseQty: "Decrease quantity",
      increaseQty: "Increase quantity",
      quantity: "Quantity",
      contactWhatsapp: "Inquire",
      noProducts: "No products available",
      productsSoon: "We will share catalog updates soon",
      backHome: "Back to main page",
    },
  },
} as const;

export const interpolate = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (result, [key, value]) => result.replace(new RegExp(`\\{${key}\\}`, "g"), value),
    template
  );

export const getUiText = <T extends keyof typeof uiText.es>(language: Language, section: T) =>
  uiText[language][section];
